import ConsumableIcon from "@/components/icons/ConsumableIcon";
import ElectricityIcon from "@/components/icons/ElectricityIcon";
import EolIcon from "@/components/icons/chapters/eolIcon";
import List from "@/components/visualizations/List/List";
import useTranslation from "@/hooks/useTranslation";
import { useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { useEmployeeImpactPerType } from "@/pages/dashboard/hooks/useEmployeeImpact";
import { formatAmountWithUnit } from "@/pages/dashboard/utils/formatters";
import { useEffect, useState } from "react";
import Widget from "../../components/Widget/Widget";
import { ImpactMetric } from "../../utils/types";

const step = 5;

export default function EmployeeImpactTopContributors({ metric }: { metric: ImpactMetric }) {
  const [limit, setLimit] = useState(step);
  const { startDate, endDate } = useDateRange();
  const translation = useTranslation();

  useEffect(() => {
    setLimit(step);
  }, [startDate, endDate]);

  const { data, isLoading, isFetching } = useEmployeeImpactPerType({
    pagination: { page: 1, pageSize: limit + 5 },
    sort: {
      key: metric.key,
      order: "descend",
    },
    metric: metric.key,
  });

  const numMore = Math.min((data?.employee_impact.length || 0) - limit);

  const listData = data?.employee_impact
    ? data?.employee_impact.slice(0, limit).map((row) => {
        return {
          icon:
            row.impact_type === "electricity" ? (
              <ElectricityIcon />
            ) : row.impact_type === "eol" ? (
              <EolIcon />
            ) : (
              <ConsumableIcon />
            ),
          label: row.name,
          value: row[metric.key],
          extraProp: {
            label: "Amount",
            value: formatAmountWithUnit(row.distance, "km", 0),
          },
          type: row.impact_type === "commute" ? "Commute" : "Business travel",
        };
      })
    : [];

  return (
    <Widget
      title={"Top contributing employee impact"}
      tooltip={translation["dashboard"]["employee"]["top_contributors"]}
    >
      <List
        loading={isLoading || isFetching}
        data={{
          list: listData,
          total: data?.company_employee_impact[metric.key],
        }}
        loadMore={{
          show: numMore > 0,
          numberOfMore: numMore,
          fn: () => setLimit(limit + step),
        }}
        format={metric.formatter}
      />
    </Widget>
  );
}
